// The questions a merchant answered No to, per base planogram, so Studio does not ask again until
// the merchant asks to see them (the panel's "1 question hidden · Show"). The
// editing history is cleared when Studio closes; a No outlives that: it is kept here, keyed like
// the history (studioHistoryStore), and written through to browser storage so it survives a reload.
import {
  deleteLocalStorageItem,
  getLocalStorageItem,
  setLocalStorageItem,
} from "src/utils/localStorage";
import { hasKeptAnswers, type KeptAnswers } from "../domain/studioTransactions";

const DECLINED_BY_KEY = new Map<string, ReadonlySet<string>>();
const STORAGE_PREFIX = "cdtStudio.declinedQuestions.";

const readStored = (key: string): ReadonlySet<string> => {
  try {
    const raw = getLocalStorageItem(`${STORAGE_PREFIX}${key}`);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(
      Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [],
    );
  } catch {
    return new Set();
  }
};

/** Flag ids answered No on this base planogram (`studioHistoryKey`); none off-route. */
export const getDeclinedQuestions = (key: string | null): ReadonlySet<string> => {
  if (key === null) return new Set();
  const known = DECLINED_BY_KEY.get(key);
  if (known) return known;
  const stored = readStored(key);
  DECLINED_BY_KEY.set(key, stored);
  return stored;
};

const storeDeclined = (key: string, next: ReadonlySet<string>): ReadonlySet<string> => {
  DECLINED_BY_KEY.set(key, next);
  try {
    setLocalStorageItem(`${STORAGE_PREFIX}${key}`, JSON.stringify([...next]));
  } catch {
    // Storage unavailable (private window, quota): the answer still holds for this page load.
  }
  return next;
};

/** Records a No for these flag ids and returns the new set. Off-route nothing is kept. */
export const declineQuestions = (
  key: string | null,
  flagIds: readonly string[],
): ReadonlySet<string> => {
  if (key === null) return new Set(flagIds);
  return storeDeclined(key, new Set([...getDeclinedQuestions(key), ...flagIds]));
};

/** Forgets the No for these flag ids, so Studio asks them again, and returns the new set. */
export const undeclineQuestions = (
  key: string | null,
  flagIds: readonly string[],
): ReadonlySet<string> => {
  if (key === null) return new Set();
  const forgotten = new Set(flagIds);
  return storeDeclined(
    key,
    new Set([...getDeclinedQuestions(key)].filter((id) => !forgotten.has(id))),
  );
};

/* The answers a merchant gives (a size layout, a size-tag Yes) live in the draft, and the draft
   lives in page memory (studioHistoryStore). They are written through to browser storage here, by
   the same key, so a reload starts from them (`withKeptAnswers`) and a later Finalize ships them. */
const KEPT_BY_KEY = new Map<string, KeptAnswers | null>();
const KEPT_PREFIX = "cdtStudio.keptAnswers.";

const readKept = (key: string): KeptAnswers | null => {
  try {
    const raw = getLocalStorageItem(`${KEPT_PREFIX}${key}`);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (typeof parsed !== "object" || parsed === null) return null;
    const { sizeLayouts, sizeTiers } = parsed as Partial<KeptAnswers>;
    const kept: KeptAnswers = {
      sizeLayouts: typeof sizeLayouts === "object" && sizeLayouts !== null ? sizeLayouts : {},
      sizeTiers: typeof sizeTiers === "object" && sizeTiers !== null ? sizeTiers : {},
    };
    return hasKeptAnswers(kept) ? kept : null;
  } catch {
    return null;
  }
};

/** The answers kept on this base planogram (`studioHistoryKey`), or null; none off-route. */
export const getKeptAnswers = (key: string | null): KeptAnswers | null => {
  if (key === null) return null;
  if (KEPT_BY_KEY.has(key)) return KEPT_BY_KEY.get(key) ?? null;
  const stored = readKept(key);
  KEPT_BY_KEY.set(key, stored);
  return stored;
};

/** Keeps the draft's answers (`keptAnswersOf`); none clears what was kept. Off-route nothing. */
export const keepAnswers = (key: string | null, kept: KeptAnswers): void => {
  if (key === null) return;
  const next = hasKeptAnswers(kept) ? kept : null;
  const known = KEPT_BY_KEY.get(key);
  if (known !== undefined && JSON.stringify(known) === JSON.stringify(next)) return;
  KEPT_BY_KEY.set(key, next);
  try {
    if (next === null) deleteLocalStorageItem(`${KEPT_PREFIX}${key}`);
    else setLocalStorageItem(`${KEPT_PREFIX}${key}`, JSON.stringify(next));
  } catch {
    // Storage unavailable (private window, quota): the answers still hold for this page load.
  }
};

/** Forgets the in-memory copies, as a page reload does. Tests only. */
export const resetDeclinedQuestionsMemory = (): void => {
  DECLINED_BY_KEY.clear();
  KEPT_BY_KEY.clear();
};
